import axiosInstance from '../server/axios';

/**
 * The production `/conversations` endpoint does not (yet) return a `last_message`
 * field. Until the backend ships that, we reconstruct the preview on the client
 * by reading each conversation's thread once and caching the result for the
 * session. Realtime events keep the cache fresh afterwards.
 */

const cache = new Map(); // conversationId -> { body, sender_id, created_at }
const inflight = new Map(); // conversationId -> Promise

export const getCachedLastMessage = (id) => cache.get(id) || null;

export const setCachedLastMessage = (id, lastMessage) => {
  if (id && lastMessage?.body != null) cache.set(id, lastMessage);
};

const fetchLastMessage = (id) => {
  if (cache.has(id)) return Promise.resolve(cache.get(id));
  if (inflight.has(id)) return inflight.get(id);
  const p = axiosInstance
    .get(`/messages/${id}`)
    .then(({ data }) => {
      const list = data.messages || [];
      const last = list[list.length - 1];
      if (last) {
        cache.set(id, {
          body: last.body,
          sender_id: last.sender?.id ?? last.sender_id,
          created_at: last.created_at,
        });
      }
      return cache.get(id) || null;
    })
    .catch(() => null)
    .finally(() => inflight.delete(id));
  inflight.set(id, p);
  return p;
};

const activityRank = (c) =>
  new Date(c.updated_at || 0).getTime() || Number(c.id) || 0;

const applyKnown = (conversation) => {
  if (conversation.last_message) return conversation;
  const cached = cache.get(conversation.id);
  if (!cached) return conversation;
  return {
    ...conversation,
    last_message: cached,
    updated_at: cached.created_at || conversation.updated_at,
  };
};

/**
 * Returns the list with `last_message` filled in wherever possible: from the
 * server field, the session cache, or (for up to `max` conversations that are
 * still missing it) a one-off thread fetch with limited concurrency.
 */
export async function withLastMessages(list, { max = 40, concurrency = 5 } = {}) {
  const base = (list || []).map(applyKnown);
  const missing = base
    .filter((c) => !c.last_message)
    // Newer conversations (higher id / later timestamp) get their preview first.
    .sort((a, b) => activityRank(b) - activityRank(a))
    .slice(0, max);
  if (!missing.length) return base;

  const queue = [...missing];
  const runNext = async () => {
    while (queue.length) {
      const conversation = queue.shift();
      await fetchLastMessage(conversation.id);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, runNext));

  return base.map(applyKnown);
}
